import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { RefreshCw, AlertTriangle, CheckCircle, Loader2, User, Shield, Bell, Send, Server, Play, Square, RotateCw, Info, Coins, Link, Search, Gauge, Zap, Activity, Target, Network } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTokenConfig } from '../contexts/TokenConfigContext';
import { getAuthenticatedAxios, getApiUrl } from '../utils/api';

const API_URL = getApiUrl();

const UserSettings = () => {
  const { user } = useAuth();
  const { tokenAddress, tokenSymbol, poolAddress, updateConfig, verifyToken, refresh: refreshTokenConfig } = useTokenConfig();
  const [restarting, setRestarting] = useState(false);
  const [restartStatus, setRestartStatus] = useState(null);
  
  // Token settings
  const [tokenSettings, setTokenSettings] = useState({
    token_address: '',
    token_symbol: '',
    pool_address: ''
  });
  const [loadingToken, setLoadingToken] = useState(false);
  const [savingToken, setSavingToken] = useState(false);
  const [verifyingToken, setVerifyingToken] = useState(false);
  const [tokenVerification, setTokenVerification] = useState(null);
  
  // Telegram notification settings
  const [telegramSettings, setTelegramSettings] = useState({
    bot_token: '',
    chat_id: '',
    threshold_buy_usd: 0.0,
    threshold_sell_usd: 0.0,
    enabled: false
  });
  const [loadingTelegram, setLoadingTelegram] = useState(false);
  const [savingTelegram, setSavingTelegram] = useState(false);
  const [testingTelegram, setTestingTelegram] = useState(false);
  
  // Server management
  const [serverStatus, setServerStatus] = useState(null);
  const [loadingServerStatus, setLoadingServerStatus] = useState(false);
  const [serverAction, setServerAction] = useState(null);

  const handleRestartService = async () => {
    if (!confirm('Вы уверены, что хотите перезапустить Market Maker сервис? Это остановит все текущие операции и перезагрузит все сервисы.')) {
      return;
    }

    setRestarting(true);
    setRestartStatus(null);

    try {
      const api = getAuthenticatedAxios();
      const response = await api.post(`${API_URL}/api/system/restart-service`);
      
      if (response.data.success) {
        setRestartStatus('success');
        toast.success('Сервис успешно перезапущен');
        
        // Через 3 секунды обновим страницу для проверки статуса
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      } else {
        setRestartStatus('error');
        toast.error(response.data.message || 'Ошибка при перезапуске сервиса');
      }
    } catch (error) {
      console.error('Error restarting service:', error);
      setRestartStatus('error');
      
      // Проверяем тип ошибки
      if (error.response?.status === 401) {
        toast.error('Ошибка авторизации. Пожалуйста, выйдите и войдите снова.');
      } else if (error.response?.status === 403) {
        toast.error('Недостаточно прав. Требуются права администратора с полным доступом.');
      } else if (error.response?.status === 404) {
        toast.error('Эндпоинт не найден. Убедитесь, что бэкенд перезапущен.');
      } else {
        toast.error(error.response?.data?.detail || error.message || 'Ошибка при перезапуске сервиса');
      }
    } finally {
      setRestarting(false);
    }
  };

  // Load settings on mount
  useEffect(() => {
    loadTelegramSettings();
    loadServerStatus();
  }, []);
  
  // Sync token settings from context
  useEffect(() => {
    if (tokenAddress && tokenSymbol) {
      setTokenSettings({
        token_address: tokenAddress,
        token_symbol: tokenSymbol,
        pool_address: poolAddress || ''
      });
    }
  }, [tokenAddress, tokenSymbol, poolAddress]);
  
  const handleVerifyToken = async () => {
    if (!tokenSettings.token_address) {
      toast.error('Введите адрес токена');
      return;
    }
    
    setVerifyingToken(true);
    setTokenVerification(null);
    
    try {
      const result = await verifyToken(tokenSettings.token_address);
      setTokenVerification(result);
      
      if (result.valid) {
        // Auto-fill symbol and pool if found
        if (result.symbol) {
          setTokenSettings(prev => ({
            ...prev,
            token_symbol: result.symbol,
            pool_address: result.pool_address || ''
          }));
        }
        toast.success(`Токен найден: ${result.name} (${result.symbol})`);
      } else {
        toast.error(result.error || 'Токен не найден');
      }
    } catch (error) {
      toast.error('Ошибка при проверке токена');
    } finally {
      setVerifyingToken(false);
    }
  };
  
  const handleSaveTokenSettings = async () => {
    if (!tokenSettings.token_address || !tokenSettings.token_symbol) {
      toast.error('Заполните адрес и тикер токена');
      return;
    }
    
    setSavingToken(true);
    
    try {
      const result = await updateConfig(
        tokenSettings.token_address,
        tokenSettings.token_symbol,
        tokenSettings.pool_address || null
      );
      
      if (result.success) {
        toast.success(`Настройки токена сохранены: ${result.config.token_symbol}`);
        refreshTokenConfig();
      } else {
        toast.error(result.error || 'Ошибка при сохранении');
      }
    } catch (error) {
      toast.error(error.message || 'Ошибка при сохранении настроек');
    } finally {
      setSavingToken(false);
    }
  };

  const loadTelegramSettings = async () => {
    try {
      setLoadingTelegram(true);
      const api = getAuthenticatedAxios();
      const response = await api.get(`${API_URL}/api/user-settings/telegram`);
      
      if (response.data.success && response.data.settings) {
        setTelegramSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error loading Telegram settings:', error);
    } finally {
      setLoadingTelegram(false);
    }
  };

  const saveTelegramSettings = async () => {
    try {
      setSavingTelegram(true);
      const api = getAuthenticatedAxios();
      const response = await api.post(`${API_URL}/api/user-settings/telegram`, telegramSettings);
      
      if (response.data.success) {
        toast.success('Настройки Telegram уведомлений сохранены');
      } else {
        toast.error('Ошибка при сохранении настроек');
      }
    } catch (error) {
      console.error('Error saving Telegram settings:', error);
      toast.error(error.response?.data?.detail || 'Ошибка при сохранении настроек');
    } finally {
      setSavingTelegram(false);
    }
  };

  const testTelegramNotification = async () => {
    try {
      setTestingTelegram(true);
      const api = getAuthenticatedAxios();
      const response = await api.post(`${API_URL}/api/user-settings/telegram/test`);
      
      if (response.data.success) {
        toast.success('Тестовое уведомление отправлено');
      } else {
        toast.error('Ошибка при отправке тестового уведомления');
      }
    } catch (error) {
      console.error('Error testing Telegram notification:', error);
      toast.error(error.response?.data?.detail || 'Ошибка при отправке тестового уведомления');
    } finally {
      setTestingTelegram(false);
    }
  };

  const loadServerStatus = async () => {
    try {
      setLoadingServerStatus(true);
      const api = getAuthenticatedAxios();
      const response = await api.get(`${API_URL}/api/user-settings/server/status`);
      if (response.data.success) {
        setServerStatus(response.data.status);
      }
    } catch (error) {
      console.error('Error loading server status:', error);
      toast.error(error.response?.data?.detail || 'Ошибка при загрузке статуса сервера');
    } finally {
      setLoadingServerStatus(false);
    }
  };

  const manageServer = async (action) => {
    if (!confirm(`Вы уверены, что хотите ${action === 'start' ? 'запустить' : action === 'stop' ? 'остановить' : 'перезапустить'} сервер?`)) {
      return;
    }

    setServerAction(action);
    try {
      const api = getAuthenticatedAxios();
      const response = await api.post(`${API_URL}/api/user-settings/server/${action}`);
      
      if (response.data.success) {
        toast.success(response.data.message || `Сервер ${action === 'start' ? 'запущен' : action === 'stop' ? 'остановлен' : 'перезапущен'} успешно`);
        setTimeout(() => {
          loadServerStatus();
          if (action === 'restart') {
            setTimeout(() => window.location.reload(), 2000);
          }
        }, 2000);
      }
    } catch (error) {
      console.error(`Error ${action}ing server:`, error);
      toast.error(error.response?.data?.detail || error.message || `Ошибка при ${action === 'start' ? 'запуске' : action === 'stop' ? 'остановке' : 'перезапуске'} сервера`);
    } finally {
      setServerAction(null);
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Настройки пользователя</h1>
          <p className="text-gray-500 mt-1">Управление настройками аккаунта и системой</p>
        </div>
      </div>

      {/* Token Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5" />
            Настройки токена
          </CardTitle>
          <CardDescription>Глобальная конфигурация рабочего токена для всей платформы</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Current Token Info */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Coins className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-blue-900 mb-2">Текущий токен</h4>
                  <div className="space-y-1 text-sm text-blue-800">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Тикер:</span>
                      <span className="font-mono bg-blue-100 px-2 py-0.5 rounded">{tokenSymbol}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Адрес:</span>
                      <span className="font-mono text-xs bg-blue-100 px-2 py-0.5 rounded break-all">{tokenAddress}</span>
                    </div>
                    {poolAddress && (
                      <div className="flex items-center gap-2">
                        <Link className="h-3 w-3" />
                        <span className="font-medium">Пул:</span>
                        <span className="font-mono text-xs bg-blue-100 px-2 py-0.5 rounded break-all">{poolAddress}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Token Address Input */}
            <div>
              <label className="block text-sm font-medium mb-2">Адрес токена (Jetton Master)</label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={tokenSettings.token_address}
                  onChange={(e) => setTokenSettings({...tokenSettings, token_address: e.target.value})}
                  placeholder="Адрес токена этого сервера"
                  className="font-mono text-xs flex-1"
                />
                <Button
                  onClick={handleVerifyToken}
                  disabled={verifyingToken || !tokenSettings.token_address}
                  variant="outline"
                  size="sm"
                >
                  {verifyingToken ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Адрес контракта Jetton Master. Нажмите кнопку поиска для проверки.
              </p>
            </div>

            {/* Verification Result */}
            {tokenVerification && (
              <div className={`p-3 rounded-lg border ${tokenVerification.valid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                {tokenVerification.valid ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="h-4 w-4" />
                      <span className="font-medium">Токен найден!</span>
                    </div>
                    <div className="text-sm text-green-600">
                      <div>Название: {tokenVerification.name}</div>
                      <div>Тикер: {tokenVerification.symbol}</div>
                      <div>Decimals: {tokenVerification.decimals}</div>
                      {tokenVerification.has_pool ? (
                        <div className="flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          Пул найден: {tokenVerification.pool_address?.slice(0, 20)}...
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-orange-600">
                          <AlertTriangle className="h-3 w-3" />
                          Пул не найден (торговля может быть недоступна)
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-red-700">
                    <AlertTriangle className="h-4 w-4" />
                    <span>{tokenVerification.error || 'Токен не найден'}</span>
                  </div>
                )}
              </div>
            )}

            {/* Token Symbol Input */}
            <div>
              <label className="block text-sm font-medium mb-2">Тикер токена</label>
              <Input
                type="text"
                value={tokenSettings.token_symbol}
                onChange={(e) => setTokenSettings({...tokenSettings, token_symbol: e.target.value.toUpperCase()})}
                placeholder="PUBG"
                className="font-mono"
                maxLength={20}
              />
              <p className="text-xs text-gray-500 mt-1">
                Короткое название токена (1-20 символов)
              </p>
            </div>

            {/* Pool Address Input */}
            <div>
              <label className="block text-sm font-medium mb-2">
                <div className="flex items-center gap-2">
                  <Link className="h-4 w-4" />
                  Адрес пула (опционально)
                </div>
              </label>
              <Input
                type="text"
                value={tokenSettings.pool_address}
                onChange={(e) => setTokenSettings({...tokenSettings, pool_address: e.target.value})}
                placeholder="Определяется автоматически при проверке токена"
                className="font-mono text-xs"
              />
              <p className="text-xs text-gray-500 mt-1">
                Адрес DEX пула для торговли. Если не указан - определяется автоматически.
              </p>
            </div>

            {/* Warning */}
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-yellow-900 mb-1">Внимание!</h4>
                  <p className="text-sm text-yellow-800">
                    После смены токена все данные балансов и аналитики будут относиться к новому токену.
                    Убедитесь, что вы проверили токен перед сохранением.
                  </p>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <Button
              onClick={handleSaveTokenSettings}
              disabled={savingToken || !tokenSettings.token_address || !tokenSettings.token_symbol}
              className="w-full"
            >
              {savingToken ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Сохранение...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Сохранить настройки токена
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* User Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Информация о пользователе
          </CardTitle>
          <CardDescription>Текущие данные вашего аккаунта</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm text-gray-500">Имя пользователя</p>
                <p className="text-lg font-semibold">{user?.username || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm text-gray-500">Роль</p>
                <p className="text-lg font-semibold capitalize">{user?.role || 'Viewer'}</p>
              </div>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm text-gray-500">Доступ</p>
                <div className="flex items-center gap-2 mt-1">
                  {user?.full_access ? (
                    <>
                      <Shield className="h-4 w-4 text-green-600" />
                      <p className="text-lg font-semibold text-green-600">Full Access</p>
                    </>
                  ) : (
                    <p className="text-lg font-semibold text-gray-600">Limited Access</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* System Management Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Управление системой
          </CardTitle>
          <CardDescription>Перезапуск Market Maker сервиса и всех используемых компонентов</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-yellow-900 mb-1">Внимание!</h4>
                  <p className="text-sm text-yellow-800">
                    Перезапуск сервиса остановит все текущие операции Market Maker, 
                    перезагрузит все используемые сервисы и скрипты. Это действие может занять 
                    несколько секунд. Убедитесь, что вы готовы к временной остановке работы системы.
                  </p>
                </div>
              </div>
            </div>

            {restartStatus === 'success' && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <div>
                    <h4 className="font-semibold text-green-900 mb-1">Сервис перезапущен</h4>
                    <p className="text-sm text-green-800">
                      Market Maker сервис успешно перезапущен. Страница будет обновлена через несколько секунд.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {restartStatus === 'error' && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  <div>
                    <h4 className="font-semibold text-red-900 mb-1">Ошибка перезапуска</h4>
                    <p className="text-sm text-red-800">
                      Не удалось перезапустить сервис. Проверьте логи или попробуйте снова.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <h4 className="font-semibold mb-1">Перезапуск Market Maker сервиса</h4>
                <p className="text-sm text-gray-600">
                  Полностью перезагрузит Market Maker сервис и все используемые компоненты системы
                </p>
              </div>
              <Button
                onClick={handleRestartService}
                disabled={restarting || restartStatus === 'success'}
                variant="destructive"
                className="ml-4"
              >
                {restarting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Перезапуск...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Перезапустить сервис
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Telegram Notifications Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Telegram Уведомления
          </CardTitle>
          <CardDescription>
            Настройка уведомлений о покупках и продажах выше указанной суммы
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingTelegram ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Bell className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-blue-900 mb-1">Информация</h4>
                    <p className="text-sm text-blue-800">
                      Уведомления будут отправляться в Telegram бот при покупке или продаже выше указанной суммы в USD.
                      Для получения Chat ID отправьте команду /start боту, затем используйте его ID.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Токен бота</label>
                  <Input
                    type="text"
                    value={telegramSettings.bot_token}
                    onChange={(e) => setTelegramSettings({...telegramSettings, bot_token: e.target.value})}
                    placeholder="1234567890:AAxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    className="font-mono text-xs"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Chat ID</label>
                  <Input
                    type="text"
                    value={telegramSettings.chat_id}
                    onChange={(e) => setTelegramSettings({...telegramSettings, chat_id: e.target.value})}
                    placeholder="123456789"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Для получения Chat ID:
                    <br />1. Найдите бота @solanabeast_bot в Telegram
                    <br />2. Отправьте команду /start
                    <br />3. Используйте ваш Telegram ID (можно получить через @userinfobot)
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Порог для покупки (USD)</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={telegramSettings.threshold_buy_usd}
                      onChange={(e) => setTelegramSettings({...telegramSettings, threshold_buy_usd: parseFloat(e.target.value) || 0})}
                      placeholder="0.00"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Минимальная сумма покупки для уведомления
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Порог для продажи (USD)</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={telegramSettings.threshold_sell_usd}
                      onChange={(e) => setTelegramSettings({...telegramSettings, threshold_sell_usd: parseFloat(e.target.value) || 0})}
                      placeholder="0.00"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Минимальная сумма продажи для уведомления
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="telegram_enabled"
                    checked={telegramSettings.enabled}
                    onChange={(e) => setTelegramSettings({...telegramSettings, enabled: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <label htmlFor="telegram_enabled" className="text-sm font-medium">
                    Включить уведомления
                  </label>
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <Button
                    onClick={saveTelegramSettings}
                    disabled={savingTelegram}
                    className="flex-1"
                  >
                    {savingTelegram ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Сохранение...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Сохранить настройки
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={testTelegramNotification}
                    disabled={testingTelegram || !telegramSettings.enabled || !telegramSettings.chat_id}
                    variant="outline"
                  >
                    {testingTelegram ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Отправка...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Тест
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Server Management Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            Управление сервером
          </CardTitle>
          <CardDescription>Управление бэкенд сервером и его статусом</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Server Status */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                {loadingServerStatus ? (
                  <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                ) : serverStatus?.running ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                )}
                <div>
                  <div className="font-medium">
                    Статус: {loadingServerStatus ? 'Проверка...' : serverStatus?.running ? 'Работает' : 'Остановлен'}
                  </div>
                  <div className="text-sm text-gray-500">
                    Порт 8000: {serverStatus?.port_8000 === 'occupied' ? 'Занят' : 'Свободен'} | 
                    Health: {serverStatus?.health_check ? 'OK' : 'FAIL'}
                    {serverStatus?.systemd_active && ' | Systemd: Active'}
                  </div>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={loadServerStatus}
                disabled={loadingServerStatus}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loadingServerStatus ? 'animate-spin' : ''}`} />
                Обновить
              </Button>
            </div>

            {/* Control Buttons */}
            <div className="grid grid-cols-3 gap-3">
              <Button
                onClick={() => manageServer('start')}
                disabled={serverAction !== null || serverStatus?.running}
                variant="outline"
                className="flex items-center gap-2"
              >
                {serverAction === 'start' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
                Запустить
              </Button>
              
              <Button
                onClick={() => manageServer('stop')}
                disabled={serverAction !== null || !serverStatus?.running}
                variant="outline"
                className="flex items-center gap-2"
              >
                {serverAction === 'stop' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Square className="h-4 w-4" />
                )}
                Остановить
              </Button>
              
              <Button
                onClick={() => manageServer('restart')}
                disabled={serverAction !== null}
                variant="outline"
                className="flex items-center gap-2"
              >
                {serverAction === 'restart' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RotateCw className="h-4 w-4" />
                )}
                Перезапустить
              </Button>
            </div>

            {/* Instructions */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start gap-2">
                <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="space-y-2 text-sm">
                  <div className="font-medium text-blue-900">Инструкция по управлению сервером:</div>
                  <ul className="list-disc list-inside space-y-1 text-blue-800">
                    <li><strong>Запустить</strong> - запускает сервер, освобождает порт 8000 и проверяет работоспособность</li>
                    <li><strong>Остановить</strong> - останавливает все процессы сервера и освобождает порт</li>
                    <li><strong>Перезапустить</strong> - полностью перезапускает сервер (рекомендуется при проблемах с авторизацией)</li>
                  </ul>
                  <div className="mt-2 pt-2 border-t border-blue-200">
                    <div className="font-medium text-blue-900">Команды для терминала:</div>
                    <div className="font-mono text-xs bg-blue-100 p-2 rounded mt-1 space-y-1">
                      <div>systemctl start tonmm-backend    # Запустить</div>
                      <div>systemctl stop tonmm-backend     # Остановить</div>
                      <div>systemctl restart tonmm-backend  # Перезапустить</div>
                      <div>systemctl status tonmm-backend   # Статус</div>
                      <div>cd /root/tonmm/TONMM/backend && bash restart_server.sh  # Ручной перезапуск</div>
                    </div>
                  </div>
                  <div className="mt-2 pt-2 border-t border-blue-200 text-xs text-blue-700">
                    <strong>Примечание:</strong> Сервер автоматически перезапускается при сбоях (через systemd) и проверяется каждые 5 минут (через cron).
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ===== Emergency Stop / Kill Switch (Phase 1.1) ===== */}
      <KillSwitchCard />

      {/* ===== Reconciliation (Phase 2.2) ===== */}
      <ReconciliationCard />

      {/* ===== BLUM Coverage (Phase 3.5) ===== */}
      <BlumCoverageCard />

      {/* ===== Rate Limiter Stats (Phase 3.0) ===== */}
      <RateLimiterStatsCard />

      {/* ===== Pending Confirmations (Phase 7) ===== */}
      <PendingConfirmationsCard />

      {/* ===== Timeline charts (Phase 8) ===== */}
      <TimelineChartsCard />
    </div>
  );
};


// ========== Kill Switch (Phase 1.1) ==========

const KillSwitchCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('manual');

  const loadStatus = async () => {
    setLoading(true);
    try {
      const api = getAuthenticatedAxios();
      const r = await api.get(`${API_URL}/api/system/emergency-status`);
      setStatus(r.data);
    } catch (e) {
      toast.error(`Emergency status load failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const emergencyStop = async () => {
    if (!window.confirm(`Экстренно остановить всю торговлю?\nПричина: ${reason}`)) return;
    setBusy(true);
    try {
      const api = getAuthenticatedAxios();
      await api.post(`${API_URL}/api/system/emergency-stop`, { reason });
      toast.success('🛑 Emergency stop activated');
      await loadStatus();
    } catch (e) {
      toast.error(`Stop failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const resume = async () => {
    if (!window.confirm('Снять kill switch и возобновить торговлю?')) return;
    setBusy(true);
    try {
      const api = getAuthenticatedAxios();
      await api.post(`${API_URL}/api/system/resume`);
      toast.success('▶️ Trading resumed');
      await loadStatus();
    } catch (e) {
      toast.error(`Resume failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    loadStatus();
    const t = setInterval(loadStatus, 15000);
    return () => clearInterval(t);
  }, []);

  const stopped = status?.stopped === true;

  return (
    <Card className={stopped ? 'border-red-500 border-2' : ''}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Zap className={`h-5 w-5 ${stopped ? 'text-red-600' : 'text-gray-600'}`} />
          Emergency Stop (Kill Switch)
        </CardTitle>
        <CardDescription>
          Экстренная остановка всех торговых операций (counter-trading, mass-trading, price-holding, grid, random-pulse). Блокировка применяется за 5 секунд.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className={`p-4 rounded-lg mb-4 ${stopped ? 'bg-red-50 border border-red-300' : 'bg-green-50 border border-green-300'}`}>
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-lg">
                {stopped ? '🛑 ОСТАНОВЛЕНО' : '✅ Работает'}
              </div>
              {stopped && (
                <div className="text-sm text-red-700 mt-1">
                  Причина: <strong>{status.reason || '—'}</strong><br />
                  Автор: {status.updated_by || '—'}<br />
                  Время: {status.updated_at || '—'}
                </div>
              )}
            </div>
            <Button onClick={loadStatus} size="sm" variant="outline" disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {!stopped && (
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium mb-1 block">Причина остановки</label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="например: аномалия цены, подозрение на баг"
              />
            </div>
            <Button
              onClick={emergencyStop}
              disabled={busy || !reason.trim()}
              variant="destructive"
              className="w-full"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
              🛑 Emergency Stop
            </Button>
          </div>
        )}

        {stopped && (
          <Button
            onClick={resume}
            disabled={busy}
            className="w-full bg-green-600 hover:bg-green-700"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
            ▶️ Возобновить торговлю
          </Button>
        )}
      </CardContent>
    </Card>
  );
};


// ========== Reconciliation (Phase 2.2) ==========

const ReconciliationCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [recalibrating, setRecalibrating] = useState(false);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const api = getAuthenticatedAxios();
      const r = await api.get(`${API_URL}/api/system/reconciliation/status`);
      setStatus(r.data);
    } catch (e) {
      toast.error(`Reconciliation status load failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const forceCheck = async () => {
    setChecking(true);
    try {
      const api = getAuthenticatedAxios();
      const r = await api.post(`${API_URL}/api/system/reconciliation/check`);
      setStatus(r.data);
      toast.success(`Check done. drift=${r.data.drift_pct.toFixed(2)}%`);
    } catch (e) {
      toast.error(`Check failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setChecking(false);
    }
  };

  const recalibrate = async () => {
    if (!window.confirm('Синхронизировать tracked := real? Это перезапишет позицию на реальные балансы кошельков.')) return;
    setRecalibrating(true);
    try {
      const api = getAuthenticatedAxios();
      const r = await api.post(`${API_URL}/api/system/reconciliation/recalibrate`, {
        reason: 'manual_recalibrate_from_ui',
      });
      toast.success(`Recalibrated: ${r.data.audit.before.tracked_tokens.toFixed(2)} → ${r.data.audit.after.tracked_tokens.toFixed(2)} tokens`);
      await loadStatus();
    } catch (e) {
      toast.error(`Recalibrate failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRecalibrating(false);
    }
  };

  useEffect(() => {
    loadStatus();
    const t = setInterval(loadStatus, 60000);
    return () => clearInterval(t);
  }, []);

  const ok = status?.ok === true;
  const drift = status?.drift_pct ?? 0;

  return (
    <Card className={!ok && status ? 'border-orange-400 border-2' : ''}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className={`h-5 w-5 ${ok ? 'text-green-600' : 'text-orange-600'}`} />
          Reconciliation (сверка позиции)
        </CardTitle>
        <CardDescription>
          Tracked (БД) vs Real (сумма балансов кошельков). При drift &gt; 1% → Telegram алерт.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {status ? (
          <div className="space-y-3">
            <div className={`p-3 rounded-lg ${ok ? 'bg-green-50 border border-green-300' : 'bg-orange-50 border border-orange-300'}`}>
              <div className="flex items-center justify-between">
                <div className="text-lg font-semibold">
                  {ok ? '✅ В синхроне' : `⚠️ Drift ${drift.toFixed(2)}%`}
                </div>
                <Button onClick={loadStatus} size="sm" variant="outline" disabled={loading}>
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Проверено: {status.checked_at || '—'}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="p-3 bg-gray-50 rounded">
                <div className="text-gray-500 text-xs">Tracked (в БД)</div>
                <div className="font-mono text-lg">{status.tracked_tokens?.toFixed(4) ?? '—'}</div>
                <div className="text-xs text-gray-400">tokens</div>
              </div>
              <div className="p-3 bg-gray-50 rounded">
                <div className="text-gray-500 text-xs">Real (в кошельках)</div>
                <div className="font-mono text-lg">{status.real_tokens?.toFixed(4) ?? '—'}</div>
                <div className="text-xs text-gray-400">tokens · {status.wallets_with_token}/{status.total_wallets} wallets держат</div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={forceCheck} disabled={checking} size="sm" variant="outline" className="flex-1">
                {checking ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Activity className="h-4 w-4 mr-1" />}
                Проверить сейчас
              </Button>
              <Button
                onClick={recalibrate}
                disabled={recalibrating || ok}
                size="sm"
                className="flex-1 bg-orange-600 hover:bg-orange-700"
              >
                {recalibrating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Target className="h-4 w-4 mr-1" />}
                Recalibrate
              </Button>
            </div>

            {!ok && (
              <div className="p-3 bg-blue-50 rounded text-xs text-blue-800 border border-blue-200">
                <strong>Recalibrate</strong> выставит tracked = real. Делать только если ты уверен, что real — правильное значение (нет пропущенных failed swaps и т.п.).
              </div>
            )}
          </div>
        ) : (
          <div className="text-gray-500 text-sm">Загрузка...</div>
        )}
      </CardContent>
    </Card>
  );
};


// ========== BLUM Coverage (Phase 3.5) ==========

const BlumCoverageCard = () => {
  const [coverage, setCoverage] = useState(null);
  const [coordStats, setCoordStats] = useState(null);
  const [monitorStats, setMonitorStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const api = getAuthenticatedAxios();
      const [cov, coord, mon] = await Promise.all([
        api.get(`${API_URL}/api/blum/coverage/status`).catch(() => ({ data: null })),
        api.get(`${API_URL}/api/blum/coordinator/stats`).catch(() => ({ data: null })),
        api.get(`${API_URL}/api/blum/router-monitor/stats`).catch(() => ({ data: null })),
      ]);
      setCoverage(cov.data);
      setCoordStats(coord.data);
      setMonitorStats(mon.data);
    } catch (e) {
      toast.error(`Load failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const forceCheck = async () => {
    setChecking(true);
    try {
      const api = getAuthenticatedAxios();
      const r = await api.post(`${API_URL}/api/blum/coverage/check`);
      setCoverage(r.data.coverage);
      const catchup = r.data.catchup || {};
      const regd = catchup.registered || 0;
      toast.success(`Coverage check done. Auto-catchup: +${regd} registered`);
    } catch (e) {
      toast.error(`Force check failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    loadAll();
    const t = setInterval(loadAll, 30000);
    return () => clearInterval(t);
  }, []);

  const covPct = coverage?.coverage_pct ?? 0;
  const missingCount = coverage?.missing_count ?? 0;

  return (
    <Card className={missingCount > 0 ? 'border-yellow-400 border-2' : ''}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Network className="h-5 w-5" />
          BLUM Coverage (обнаружение новых токенов)
        </CardTitle>
        <CardDescription>
          4 слоя защиты: blockchain_scanner (2s) + router_monitor (1s) + auto_discovery (60s, до 50k offset) + coverage_service (5min auto-catchup).
        </CardDescription>
      </CardHeader>
      <CardContent>
        {coverage && (
          <>
            <div className={`p-3 rounded-lg mb-3 ${missingCount === 0 ? 'bg-green-50 border border-green-300' : 'bg-yellow-50 border border-yellow-300'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-lg font-semibold">
                    {missingCount === 0 ? '✅ 100% покрытие' : `⚠️ ${missingCount} токенов не в БД`}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    Coverage: <strong>{covPct}%</strong> ·
                    Known in DB: <strong>{coverage.known_in_db_total}</strong> ·
                    Router jettons: <strong>{coverage.router_jettons_total}</strong>
                  </div>
                </div>
                <Button onClick={forceCheck} disabled={checking} size="sm">
                  {checking ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                  Force check
                </Button>
              </div>
            </div>

            {coordStats && (
              <div className="grid grid-cols-4 gap-2 text-sm mb-3">
                <div className="p-2 bg-gray-50 rounded">
                  <div className="text-xs text-gray-500">Registered</div>
                  <div className="font-mono text-lg text-green-700">{coordStats.registered || 0}</div>
                </div>
                <div className="p-2 bg-gray-50 rounded">
                  <div className="text-xs text-gray-500">Already known</div>
                  <div className="font-mono text-lg">{coordStats.already_known || 0}</div>
                </div>
                <div className="p-2 bg-gray-50 rounded">
                  <div className="text-xs text-gray-500">Rejected (не BCL)</div>
                  <div className="font-mono text-lg text-gray-600">{coordStats.rejected_not_bcl || 0}</div>
                </div>
                <div className="p-2 bg-gray-50 rounded">
                  <div className="text-xs text-gray-500">Errors</div>
                  <div className={`font-mono text-lg ${(coordStats.errors || 0) > 0 ? 'text-red-600' : ''}`}>{coordStats.errors || 0}</div>
                </div>
              </div>
            )}

            {coordStats?.by_source && Object.keys(coordStats.by_source).length > 0 && (
              <div className="text-xs text-gray-600 mb-3">
                <strong>By source:</strong>{' '}
                {Object.entries(coordStats.by_source)
                  .map(([s, c]) => `${s}: ${c}`)
                  .join(' · ')}
              </div>
            )}

            {monitorStats && (
              <div className="text-xs text-gray-600">
                Router Monitor: {monitorStats.running ? '🟢 running' : '🔴 stopped'} ·
                ticks: {monitorStats.ticks || 0} ·
                actions: {monitorStats.actions_processed || 0} ·
                candidates: {monitorStats.candidates_registered || 0}
              </div>
            )}

            {missingCount > 0 && coverage.missing_addresses?.length > 0 && (
              <div className="mt-3 p-3 bg-yellow-50 rounded text-xs">
                <strong>Sample missing (auto-catchup уже запустил register):</strong>
                <ul className="mt-1 space-y-0.5 font-mono">
                  {coverage.missing_addresses.slice(0, 5).map((a) => (
                    <li key={a}>{a.substring(0, 40)}...</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};


const RateLimiterStatsCard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  const loadStats = async () => {
    setLoading(true);
    try {
      const api = getAuthenticatedAxios();
      const r = await api.get(`${API_URL}/api/system/rate-limiter/stats`);
      setStats(r.data);
      setLastUpdate(new Date());
    } catch (e) {
      toast.error(`Failed to load rate limiter stats: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resetStats = async () => {
    if (!window.confirm('Сбросить счётчики rate-limiter?')) return;
    try {
      const api = getAuthenticatedAxios();
      await api.post(`${API_URL}/api/system/rate-limiter/reset-stats`);
      toast.success('Stats reset');
      await loadStats();
    } catch (e) {
      toast.error(`Reset failed: ${e.message}`);
    }
  };

  useEffect(() => {
    loadStats();
    const t = setInterval(loadStats, 10000);
    return () => clearInterval(t);
  }, []);

  const suggestionColor = (s) => {
    if (!s) return 'text-gray-600';
    if (s.includes('rate_limited_by_upstream')) return 'text-red-600 font-semibold';
    if (s.includes('увеличить квоту')) return 'text-orange-600 font-semibold';
    if (s.includes('на пределе')) return 'text-yellow-700';
    return 'text-green-600';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gauge className="h-5 w-5" />
          Rate Limiter (нагрузка на внешние API)
        </CardTitle>
        <CardDescription>
          Статистика запросов к TonAPI и Toncenter. Если utilization выше 70% или есть rate_limited от upstream — пора поднимать квоту тарифа.
          {lastUpdate && <span className="ml-2 text-xs text-gray-500">Обновлено: {lastUpdate.toLocaleTimeString()}</span>}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2 mb-4">
          <Button onClick={loadStats} size="sm" variant="outline" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Обновить
          </Button>
          <Button onClick={resetStats} size="sm" variant="outline">
            Сбросить счётчики
          </Button>
        </div>

        {stats && Object.keys(stats.limiters || {}).length === 0 && (
          <div className="text-gray-500 text-sm">
            Limiter'ы ещё не инициализированы — нужно чтобы какой-то сервис сделал первый запрос.
          </div>
        )}

        {stats && Object.keys(stats.limiters || {}).length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2">Провайдер</th>
                  <th className="py-2">Лимит RPS</th>
                  <th className="py-2">Запросов</th>
                  <th className="py-2">Ждали</th>
                  <th className="py-2">Utilization</th>
                  <th className="py-2">429 от upstream</th>
                  <th className="py-2">Макс. ожидание</th>
                  <th className="py-2">Рекомендация</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(stats.limiters).map(([name, s]) => (
                  <tr key={name} className="border-b hover:bg-gray-50">
                    <td className="py-2 font-mono">{name}</td>
                    <td className="py-2">{s.rps_limit}</td>
                    <td className="py-2">{s.requests}</td>
                    <td className="py-2">{s.waited_requests}</td>
                    <td className="py-2">
                      <span className={s.utilization_pct > 70 ? 'text-red-600 font-semibold' : s.utilization_pct > 40 ? 'text-orange-600' : 'text-green-600'}>
                        {s.utilization_pct}%
                      </span>
                    </td>
                    <td className="py-2">
                      {s.rate_limited_responses > 0
                        ? <span className="text-red-600 font-semibold">{s.rate_limited_responses}</span>
                        : '0'}
                    </td>
                    <td className="py-2">{s.max_wait_seconds}s</td>
                    <td className={`py-2 ${suggestionColor(stats.suggestions?.[name])}`}>
                      {stats.suggestions?.[name] || 'ok'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200 text-xs text-blue-800">
          <div className="font-semibold mb-1">Как читать:</div>
          <ul className="list-disc list-inside space-y-1">
            <li><strong>Utilization</strong> — % запросов которым пришлось ждать из-за лимита. Если &gt; 70% стабильно — квоту надо поднимать.</li>
            <li><strong>429 от upstream</strong> — сколько раз провайдер уже отклонил наш запрос несмотря на наш внутренний лимит. Если &gt; 0 — срочно поднять квоту.</li>
            <li><strong>Макс. ожидание</strong> — пиковое время ожидания одного запроса. Если &gt; 1-2 сек — пользовательские операции будут подтормаживать.</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

// ========== Pending Confirmations (Phase 7) ==========

const PendingConfirmationsCard = () => {
  const [pending, setPending] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const api = getAuthenticatedAxios();
      const [p, s] = await Promise.all([
        api.get(`${API_URL}/api/system/confirmations/pending?limit=20`),
        api.get(`${API_URL}/api/system/confirmations/stats`),
      ]);
      setPending(p.data.pending || []);
      setStats(s.data);
    } catch (e) {
      toast.error(`Confirmations load failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const fmtAge = (submittedIso) => {
    if (!submittedIso) return '—';
    const ageSec = (Date.now() - new Date(submittedIso).getTime()) / 1000;
    if (ageSec < 60) return `${ageSec.toFixed(0)}s`;
    if (ageSec < 3600) return `${(ageSec / 60).toFixed(1)}m`;
    return `${(ageSec / 3600).toFixed(1)}h`;
  };

  const truncate = (s, n = 16) => (s ? `${s.substring(0, n)}...` : '—');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5" />
          Pending Confirmations (Phase 2.4)
        </CardTitle>
        <CardDescription>
          Swap'ы, ожидающие on-chain подтверждения. Timeout 180 сек → status=failed_unconfirmed + rollback position.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {stats && (
          <div className="grid grid-cols-5 gap-2 text-sm mb-4">
            <div className="p-2 bg-gray-50 rounded">
              <div className="text-xs text-gray-500">Всего за 24ч</div>
              <div className="font-mono text-lg">{stats.total}</div>
            </div>
            <div className="p-2 bg-green-50 rounded">
              <div className="text-xs text-green-700">Confirmed</div>
              <div className="font-mono text-lg text-green-800">{stats.confirmed}</div>
            </div>
            <div className="p-2 bg-yellow-50 rounded">
              <div className="text-xs text-yellow-700">Pending</div>
              <div className="font-mono text-lg text-yellow-800">{stats.pending}</div>
            </div>
            <div className="p-2 bg-red-50 rounded">
              <div className="text-xs text-red-700">Failed (rollback)</div>
              <div className="font-mono text-lg text-red-800">{stats.failed_unconfirmed}</div>
            </div>
            <div className="p-2 bg-blue-50 rounded">
              <div className="text-xs text-blue-700">Confirmation rate</div>
              <div className="font-mono text-lg text-blue-800">{stats.confirmation_rate_pct}%</div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold">Ожидающие подтверждения ({pending.length}):</div>
          <Button onClick={load} size="sm" variant="outline" disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {pending.length === 0 ? (
          <div className="text-gray-500 text-sm py-4 text-center">
            Нет pending confirmations. Либо сервис ещё не подхватил swap'ы, либо все подтверждены.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-1">TX hash</th>
                  <th className="py-1">Action</th>
                  <th className="py-1">Wallet</th>
                  <th className="py-1">Expected</th>
                  <th className="py-1">Age</th>
                  <th className="py-1">Attempts</th>
                  <th className="py-1">Source</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((p) => (
                  <tr key={p._id} className="border-b hover:bg-gray-50">
                    <td className="py-1 font-mono">{truncate(p.tx_hash)}</td>
                    <td className="py-1">
                      <span className={p.action === 'buy' ? 'text-green-700' : 'text-red-700'}>
                        {p.action}
                      </span>
                    </td>
                    <td className="py-1 font-mono">{truncate(p.wallet_address, 18)}</td>
                    <td className="py-1 font-mono">{p.expected_ask_units ? (p.expected_ask_units / 1e9).toFixed(4) : '—'}</td>
                    <td className="py-1">{fmtAge(p.submitted_at)}</td>
                    <td className="py-1">{p.attempts || 0}</td>
                    <td className="py-1 text-gray-600">{p.strategy_id || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};


// ========== Timeline charts (Phase 8) ==========

const TimelineChartsCard = () => {
  const [positionData, setPositionData] = useState([]);
  const [reconcileData, setReconcileData] = useState([]);
  const [hours, setHours] = useState(24);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const api = getAuthenticatedAxios();
      const [pos, rec] = await Promise.all([
        api.get(`${API_URL}/api/system/position/history?hours=${hours}&limit=500`),
        api.get(`${API_URL}/api/system/reconciliation/history?hours=${hours}&limit=200`),
      ]);
      // Подготовить для recharts: берём только позицию after + timestamp (epoch millis)
      setPositionData(
        (pos.data.records || []).map((r) => ({
          ts: r.timestamp ? new Date(r.timestamp).getTime() : 0,
          position_usd: r.position_after,
          volume_usd: r.volume_usd * (r.operation_type === 'buy' ? 1 : -1),
          operation: r.operation_type,
        }))
      );
      setReconcileData(
        (rec.data.records || []).map((r) => ({
          ts: r.checked_at ? new Date(r.checked_at).getTime() : 0,
          drift_pct: r.drift_pct,
          tracked: r.tracked_tokens,
          real: r.real_tokens,
        }))
      );
    } catch (e) {
      toast.error(`Timeline load failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [hours]);

  // Recharts lazy-loaded чтобы не блокировать инициал-рендер страницы если
  // пакет по каким-то причинам проблемный.
  const [Recharts, setRecharts] = useState(null);
  useEffect(() => {
    import('recharts')
      .then((m) => setRecharts(m))
      .catch(() => setRecharts(false));
  }, []);

  const fmtTs = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleString('ru-RU', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5" />
          History timelines
        </CardTitle>
        <CardDescription>
          Net position (из smart_position_history) и Reconciliation drift (из reconciliation_history) за выбранный период.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2 mb-4">
          <span className="text-sm text-gray-600">Period:</span>
          {[1, 6, 24, 72, 168].map((h) => (
            <Button
              key={h}
              size="sm"
              variant={hours === h ? 'default' : 'outline'}
              onClick={() => setHours(h)}
            >
              {h}h
            </Button>
          ))}
          <Button onClick={load} size="sm" variant="outline" disabled={loading} className="ml-auto">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        <div className="space-y-4">
          <div>
            <div className="text-sm font-semibold mb-1">
              Net position (USD) · {positionData.length} records
            </div>
            {Recharts && positionData.length > 0 ? (
              <div style={{ width: '100%', height: 220 }}>
                <Recharts.ResponsiveContainer>
                  <Recharts.LineChart data={positionData}>
                    <Recharts.CartesianGrid strokeDasharray="3 3" />
                    <Recharts.XAxis dataKey="ts" tickFormatter={fmtTs} minTickGap={40} fontSize={11} />
                    <Recharts.YAxis fontSize={11} />
                    <Recharts.Tooltip labelFormatter={fmtTs} />
                    <Recharts.Line
                      type="monotone"
                      dataKey="position_usd"
                      stroke="#2563eb"
                      strokeWidth={2}
                      dot={false}
                      name="Net position USD"
                    />
                  </Recharts.LineChart>
                </Recharts.ResponsiveContainer>
              </div>
            ) : (
              <div className="text-gray-500 text-xs py-4">
                {Recharts === false
                  ? 'Не удалось загрузить recharts'
                  : positionData.length === 0
                    ? `Нет записей за последние ${hours}ч`
                    : 'Загрузка chart...'}
              </div>
            )}
          </div>

          <div>
            <div className="text-sm font-semibold mb-1">
              Reconciliation drift (%) · {reconcileData.length} records
            </div>
            {Recharts && reconcileData.length > 0 ? (
              <div style={{ width: '100%', height: 220 }}>
                <Recharts.ResponsiveContainer>
                  <Recharts.AreaChart data={reconcileData}>
                    <Recharts.CartesianGrid strokeDasharray="3 3" />
                    <Recharts.XAxis dataKey="ts" tickFormatter={fmtTs} minTickGap={40} fontSize={11} />
                    <Recharts.YAxis fontSize={11} />
                    <Recharts.Tooltip labelFormatter={fmtTs} />
                    <Recharts.Area
                      type="monotone"
                      dataKey="drift_pct"
                      stroke="#ea580c"
                      fill="#fed7aa"
                      name="Drift %"
                    />
                  </Recharts.AreaChart>
                </Recharts.ResponsiveContainer>
              </div>
            ) : (
              <div className="text-gray-500 text-xs py-4">
                {Recharts === false
                  ? 'Recharts недоступен'
                  : reconcileData.length === 0
                    ? `Нет reconciliation snapshots за ${hours}ч — сервис запускается раз в 10 мин`
                    : 'Загрузка chart...'}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};


export default UserSettings;

